// src/services/analytics.ts

type AnalyticsEventParams = {
  category: string
  label?: string
  value?: number
  [key: string]: string | number | boolean | undefined
}

class AnalyticsService {
  private gaId: string | null = null
  private isEnabled: boolean = false
  private startTime: number = Date.now()

  constructor() {
    console.log('🔧 Analytics Service 初始化开始...')

    this.gaId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID || null
    this.isEnabled = import.meta.env.VITE_ENABLE_ANALYTICS === 'true' && !!this.gaId

    console.log('🔧 Analytics 配置检查:', {
      gaId: this.gaId,
      enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS,
      isEnabled: this.isEnabled,
      allEnvVars: {
        VITE_GOOGLE_ANALYTICS_ID: import.meta.env.VITE_GOOGLE_ANALYTICS_ID,
        VITE_ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS,
      },
    })

    if (this.isEnabled && this.gaId) {
      console.log('✅ Analytics 满足启用条件，开始初始化 GA...')
      this.initializeGA()
    } else {
      console.log('❌ Analytics 未启用:', {
        reason: !this.gaId ? 'Missing GA ID' : 'Analytics disabled',
      })
    }
  }

  private initializeGA(): void {
    if (!this.gaId) {
      console.error('❌ GA ID 为空，无法初始化')
      return
    }

    console.log('🚀 开始加载 Google Analytics 脚本...')

    try {
      // 动态加载GA脚本
      const script1 = document.createElement('script')
      script1.async = true
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`
      script1.onload = () => {
        console.log('✅ GA 外部脚本加载完成')
      }
      script1.onerror = (error) => {
        console.error('❌ GA 外部脚本加载失败:', error)
      }
      document.head.appendChild(script1)

      // 初始化gtag
      const script2 = document.createElement('script')
      script2.innerHTML = `
        console.log('🔧 正在初始化 gtag...');
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag(){window.dataLayer.push(arguments);}
        window.gtag('js', new Date());
        window.gtag('config', '${this.gaId}', {
          send_page_view: true,
          anonymize_ip: true
        });
        console.log('✅ gtag 初始化完成');
      `
      document.head.appendChild(script2)

      console.log('📊 Google Analytics 初始化完成:', this.gaId)
    } catch (error) {
      console.error('❌ GA 初始化出错:', error)
    }
  }

  trackEvent(action: string, params: AnalyticsEventParams): void {
    if (!this.isEnabled || typeof window === 'undefined' || !window.gtag) return

    const { category, label, value, ...customParams } = params
    const eventParams: Record<string, string | number | boolean | undefined> = {
      event_category: category,
      ...customParams,
    }

    if (label) eventParams.event_label = label
    if (typeof value === 'number') eventParams.value = value

    try {
      window.gtag('event', action, eventParams)
      console.log('📊 事件追踪成功:', action, eventParams)
    } catch (error) {
      console.error('❌ 事件追踪失败:', action, error)
    }
  }

  // 页面访问追踪
  trackPageView(page_title?: string, page_location?: string): void {
    console.log('📊 尝试追踪页面访问:', {
      isEnabled: this.isEnabled,
      hasGtag: typeof window !== 'undefined' && !!window.gtag,
      pageTitle: page_title || document.title,
      pageLocation: page_location || window.location.href,
    })

    if (!this.isEnabled) {
      console.log('⚠️ Analytics 未启用，跳过页面访问追踪')
      return
    }

    if (typeof window === 'undefined' || !window.gtag) {
      console.log('⚠️ gtag 未就绪，跳过页面访问追踪')
      return
    }

    try {
      window.gtag('config', this.gaId!, {
        page_title: page_title || document.title,
        page_location: page_location || window.location.href,
        send_page_view: true,
      })

      console.log('✅ 页面访问追踪成功:', page_title || document.title)
    } catch (error) {
      console.error('❌ 页面访问追踪失败:', error)
    }
  }

  // 停留时间追踪 (当用户离开页面时调用)
  trackTimeOnSite(): void {
    if (!this.isEnabled || typeof window === 'undefined' || !window.gtag) return

    const timeSpent = Math.round((Date.now() - this.startTime) / 1000) // 秒

    try {
      window.gtag('event', 'page_view_time', {
        event_category: 'engagement',
        event_label: 'time_on_site',
        value: timeSpent,
        custom_parameters: {
          time_spent_seconds: timeSpent,
          time_spent_minutes: Math.round(timeSpent / 60),
        },
      })

      console.log('📊 停留时间追踪成功:', timeSpent, '秒')
    } catch (error) {
      console.error('❌ 停留时间追踪失败:', error)
    }
  }

  // 聊天交互追踪 (可选)
  trackChatInteraction(action: string): void {
    this.trackEvent('chat_interaction', {
      category: 'engagement',
      label: action,
      interaction_type: action,
    })
  }

  // 获取基本信息
  getInfo(): { enabled: boolean; gaId: string | null } {
    return {
      enabled: this.isEnabled,
      gaId: this.gaId,
    }
  }
}

// 创建单例实例
console.log('🔧 正在创建 Analytics Service 实例...')
export const analyticsService = new AnalyticsService()

// 页面卸载时追踪停留时间
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    console.log('📊 页面卸载，追踪停留时间')
    analyticsService.trackTimeOnSite()
  })

  // 页面隐藏时也追踪（处理单页应用的情况）
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      console.log('📊 页面隐藏，追踪停留时间')
      analyticsService.trackTimeOnSite()
    }
  })
}

export default analyticsService
